import { useState, useEffect, useRef } from "react";
import "./messagingHeader.css";
import { FaXmark, FaMinus, FaExpand, FaCompress } from "react-icons/fa6";

import { CONVERSATION_CONSTANTS } from "../helpers/constants";
import { t, useAwacLang } from "../awac/i18n";
import { getAwacState, setAwacState } from "../awac/awacSessionState";

export default function MessagingHeader(props) {
    // Whether the "End this conversation?" confirmation panel is visible.
    const [showEndConfirm, setShowEndConfirm] = useState(false);
    const cancelButtonRef = useRef(null);
    // Re-render when the page language changes (FR default, Bill 96).
    useAwacLang();

    // Booking status chip — confirmed visit details, persisted for the
    // session so the chip survives minimize/expand and page reloads.
    const [booking, setBooking] = useState(() => getAwacState("booking"));

    // Listen for booking confirmations dispatched by the BookingConfirm
    // template and persist them so the chip can be restored on re-mount.
    useEffect(() => {
        function handleBookingConfirmed(evt) {
            const detail = evt && evt.detail;
            if (detail && detail.date && detail.time) {
                setBooking(detail);
                setAwacState("booking", detail);
            }
        }
        window.addEventListener("awac:booking-confirmed", handleBookingConfirmed);
        return () => window.removeEventListener("awac:booking-confirmed", handleBookingConfirmed);
    }, []);

    // Hide the confirmation if the conversation is no longer open
    // (e.g. the agent or the server ended it while the panel was visible).
    useEffect(() => {
        if (props.conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            setShowEndConfirm(false);
        }
    }, [props.conversationStatus]);

    // Move focus to the safe choice (Cancel) when the panel opens, and
    // dismiss on Escape.
    useEffect(() => {
        if (!showEndConfirm) {
            return undefined;
        }
        if (cancelButtonRef.current) {
            cancelButtonRef.current.focus();
        }
        function handleKeyDown(evt) {
            if (evt.key === "Escape") {
                setShowEndConfirm(false);
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [showEndConfirm]);
    /**
     * Handle the Minimize ('—') button click. Collapses the window to the floating
     * launcher bubble WITHOUT ending the conversation, so the chat (and its live
     * connection) is preserved and can be re-expanded by clicking the bubble.
     * @param {object} evt - click event from the Minimize button
     */
    function handleMinimizeButtonClick(evt) {
        if (evt && typeof props.minimizeMessagingWindow === "function") {
            props.minimizeMessagingWindow();
        }
    }

    /**
     * Handle Close ('X') button click based on the current conversation status.
     * If the conversation is open, show a confirmation panel before ending it
     * (ending is irreversible, so we never end on a single click).
     * If the conversation is either closed or not yet started, invoke the parent's handler to close the messaging window.
     * @param {object} evt - click event from the Close ('X') button
     */
    function handleCloseButtonClick(evt) {
        if (evt) {
            if (props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
                // Toggle the confirmation panel instead of ending immediately.
                setShowEndConfirm((visible) => !visible);
            } else if (props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION || props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION) {
                // Close the messaging window if the conversation is in closed state or not yet started.
                props.closeMessagingWindow();
            }
        }
    }

    /**
     * User confirmed — actually end the conversation.
     */
    function handleConfirmEndConversation() {
        setShowEndConfirm(false);
        props.endConversation();
    }

    /**
     * Generates a title text for the header Close ('X') button based on the current conversation status.
     * @returns {string}
     */
    function generateCloseButtonTitle() {
        return props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION ? t("chat.close") : t("chat.closeWindow");
    }

    const isConversationOpen = props.conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION;

    return (
        <div className="messagingHeader">
            <div className="messagingHeaderBrand">
                <span className="messagingHeaderStatusDot" aria-hidden="true"></span>
                <span className="messagingHeaderTitle">Lia</span>
                <span className="messagingHeaderSubtitle">{t("chat.subtitle")}</span>
                {booking && booking.date && booking.time && (
                    <span
                        className="messagingHeaderBookedChip"
                        title={t("chat.bookedChip")}
                        aria-label={`${t("chat.bookedChip")} — ${booking.date} ${booking.time}`}>
                        <span aria-hidden="true">✓</span> {booking.date} · {booking.time}
                    </span>
                )}
            </div>
            <div className="messagingHeaderActions">
                {typeof props.onToggleFullscreen === "function" && (
                    <button
                        className="messagingHeaderCloseButton messagingHeaderFullscreenButton"
                        title={props.isFullscreen ? t("chat.exitFullscreen") : t("chat.fullscreen")}
                        aria-label={props.isFullscreen ? t("chat.exitFullscreen") : t("chat.fullscreen")}
                        aria-pressed={props.isFullscreen ? "true" : "false"}
                        onClick={props.onToggleFullscreen}>
                        {props.isFullscreen
                            ? <FaCompress className="messagingHeaderCloseButtonIcon"/>
                            : <FaExpand className="messagingHeaderCloseButtonIcon"/>}
                    </button>
                )}
                <button
                    className="messagingHeaderCloseButton messagingHeaderMinimizeButton"
                    title={t("chat.minimize")}
                    aria-label={t("chat.minimize")}
                    onClick={handleMinimizeButtonClick}>
                    <FaMinus className="messagingHeaderCloseButtonIcon"/>
                </button>
                <button
                    className="messagingHeaderCloseButton"
                    title={generateCloseButtonTitle()}
                    aria-label={generateCloseButtonTitle()}
                    aria-expanded={isConversationOpen ? showEndConfirm : undefined}
                    onClick={handleCloseButtonClick}>
                    <FaXmark className="messagingHeaderCloseButtonIcon"/>
                </button>
            </div>
            {showEndConfirm && isConversationOpen && (
                <>
                    <div
                        className="messagingHeaderConfirmBackdrop"
                        onClick={() => setShowEndConfirm(false)}
                        aria-hidden="true">
                    </div>
                    <div
                        className="messagingHeaderConfirmPanel"
                        role="alertdialog"
                        aria-label={t("chat.endTitle")}>
                        <p className="messagingHeaderConfirmText">
                            {t("chat.endTitle")}
                            <span className="messagingHeaderConfirmHint">{t("chat.endBody")}</span>
                        </p>
                        <div className="messagingHeaderConfirmActions">
                            <button
                                ref={cancelButtonRef}
                                className="messagingHeaderConfirmButton messagingHeaderConfirmCancel"
                                onClick={() => setShowEndConfirm(false)}>
                                {t("chat.endCancel")}
                            </button>
                            <button
                                className="messagingHeaderConfirmButton messagingHeaderConfirmEnd"
                                onClick={handleConfirmEndConversation}>
                                {t("chat.endConfirm")}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}